import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isIOS, openAssessmentPdfIOS } from "@/lib/pdf";

const IOS = isIOS();
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ExerciseImage } from "@/components/ExerciseImage";
import { Dumbbell, LogOut, CalendarDays, Activity, FileDown, Loader2, ClipboardList, Image as ImageIcon } from "lucide-react";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const formatDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export default function AlunoView() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [today, setToday] = useState({ workouts: [], weekday: 0, date: "" });
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewEx, setViewEx] = useState(null); // { name, ex, image_url }
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, t, a] = await Promise.all([
          api.get("/aluno/me"),
          api.get("/aluno/today"),
          api.get("/aluno/assessments"),
        ]);
        setMe(m.data);
        setToday(t.data);
        setAssessments(a.data || []);
      } catch (e) {
        toast.error("Não foi possível carregar seus dados.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadPdf = async (id) => {
    setDownloading(id);
    try {
      if (IOS) { await openAssessmentPdfIOS(id); return; }
      const res = await api.get(`/assessments/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "avaliacao.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao baixar o PDF.");
    } finally {
      setDownloading(null);
    }
  };

  const workouts = today.workouts || [];

  return (
    <div data-testid="aluno-view" className="min-h-screen bg-[#0A0D12]">
      <header className="border-b border-[#263147] bg-[#0A0D12]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Dumbbell className="w-5 h-5 text-[#0A0D12]" />
            </div>
            <span className="text-lg font-bold font-heading text-slate-100">FitTrainer</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 hidden sm:block">{user?.name || user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="aluno-logout-button"
              className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg">
              <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1">
            Olá, {me?.student?.nickname || me?.student?.full_name || "Aluno"}! 👋
          </h1>
          {me?.professor && (
            <p className="text-sm text-slate-400">Professor(a): <span className="text-slate-200">{me.professor.name}</span></p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
        ) : (
          <>
            {/* Treino do dia */}
            <section data-testid="aluno-today" className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 sm:p-7">
              <h2 className="text-lg sm:text-xl font-semibold font-heading text-emerald-400 flex items-center gap-2.5 mb-1">
                <span className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center"><CalendarDays className="w-5 h-5" /></span>
                Treino de hoje
              </h2>
              <p className="text-sm text-slate-400 mb-5">{WEEKDAYS[today.weekday] || ""} • {formatDateBR(today.date)}</p>

              {workouts.length === 0 ? (
                <div data-testid="aluno-no-workout" className="text-center py-10 text-slate-500 text-sm">
                  Nenhum treino agendado para hoje. Aproveite o descanso! 💪
                </div>
              ) : (
                <div className="space-y-4">
                  {workouts.map((w) => {
                    const wk = w.workout || {};
                    const items = wk.items || [];
                    return (
                      <div key={w.id} data-testid="aluno-workout-card" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <ClipboardList className="w-4 h-4 text-emerald-400" />
                          <span className="text-slate-200 font-medium">{wk.model_name || "Treino"}</span>
                          <span className="ml-auto text-xs text-slate-500 tabular-nums">{w.time}</span>
                        </div>
                        {wk.manual_text && <p className="text-sm text-slate-300 whitespace-pre-wrap mb-3">{wk.manual_text}</p>}
                        {items.length > 0 && (
                          <div className="space-y-1.5">
                            {items.map((it, i) => (
                              <button type="button" key={i} data-testid={`aluno-exercise-${w.id}-${i}`}
                                onClick={() => setViewEx({ name: it.exercise_name, ex: it, image_url: it.image_url || null })}
                                className="w-full text-left flex items-center gap-2 text-sm bg-[#181F2C] border border-[#263147] rounded-lg px-2 py-2 hover:border-emerald-500/50 transition-colors">
                                <div className="w-10 h-10 shrink-0 rounded-md bg-[#0F1520] border border-[#263147] overflow-hidden flex items-center justify-center">
                                  {it.image_url ? <ExerciseImage value={it.image_url} alt={it.exercise_name} testId={`aluno-exercise-thumb-${w.id}-${i}`} className="w-full h-full object-cover" />
                                    : <ImageIcon className="w-4 h-4 text-slate-700" />}
                                </div>
                                <span className="text-slate-200 flex-1 truncate">{it.exercise_name}</span>
                                {it.sets && <span className="text-slate-400 tabular-nums">{it.sets}x{it.reps || ""}</span>}
                                {it.load_note && <span className="text-emerald-400 text-xs">{it.load_note}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Exercise demo (image/GIF from Library) — same pattern as AgendaDia */}
            <Dialog open={!!viewEx} onOpenChange={(o) => !o && setViewEx(null)}>
              <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-md" data-testid="exercise-view-modal">
                {viewEx && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="font-heading">{viewEx.name}</DialogTitle>
                      {viewEx.ex?.muscle_group && <DialogDescription className="text-slate-400">{viewEx.ex.muscle_group}{viewEx.ex.equipment ? ` • ${viewEx.ex.equipment}` : ""}</DialogDescription>}
                    </DialogHeader>
                    <div className="py-1">
                      <div className="w-full rounded-xl bg-[#0F1520] border border-[#263147] overflow-hidden flex items-center justify-center" style={{ minHeight: 200 }}>
                        {viewEx.image_url ? (
                          <ExerciseImage value={viewEx.image_url} alt={viewEx.name} minHeight={200} testId="exercise-view-image" className="w-full object-contain max-h-[60vh]" />
                        ) : (
                          <div className="flex flex-col items-center gap-2 py-12 text-slate-600"><ImageIcon className="w-10 h-10" /><span className="text-xs">Sem imagem/GIF cadastrado na Biblioteca</span></div>
                        )}
                      </div>
                      {viewEx.ex?.description && <p className="text-sm text-slate-400 mt-3">{viewEx.ex.description}</p>}
                    </div>
                    <DialogFooter>
                      <Button data-testid="exercise-view-close" onClick={() => setViewEx(null)} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">Fechar</Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>

            {/* Avaliações */}
            <section data-testid="aluno-assessments" className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 sm:p-7">
              <h2 className="text-lg sm:text-xl font-semibold font-heading text-emerald-400 flex items-center gap-2.5 mb-5">
                <span className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center"><Activity className="w-5 h-5" /></span>
                Minhas avaliações físicas
              </h2>
              {assessments.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">Nenhuma avaliação registrada ainda.</div>
              ) : (
                <div className="space-y-2">
                  {assessments.map((a) => (
                    <div key={a.id} data-testid="aluno-assessment-row" className="flex items-center gap-3 bg-[#0F1520] border border-[#263147] rounded-xl px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-200 text-sm font-medium">Avaliação de {formatDateBR(a.created_at)}</p>
                        {a.bioimpedance?.imc && <p className="text-xs text-slate-500">IMC: {a.bioimpedance.imc}</p>}
                      </div>
                      <Button type="button" size="sm" data-testid="aluno-assessment-pdf" disabled={downloading === a.id}
                        onClick={() => downloadPdf(a.id)}
                        className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-3">
                        {downloading === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 sm:mr-1" /><span className={IOS ? "ml-1" : "hidden sm:inline"}>{IOS ? "Ver PDF" : "PDF"}</span></>}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
